import { useState, useEffect, useCallback, useRef } from 'react';
import { useWorkflowMetadata } from '../contexts/WorkflowMetadataContext';

interface UseWorkflowInterruptOptions {
  enabled?: boolean;
  onInterrupt?: (reason?: string) => void;
  onResume?: () => void;
}

export const useWorkflowInterrupt = (options: UseWorkflowInterruptOptions = {}) => {
  const { enabled = true, onInterrupt, onResume } = options;
  const { interrupt, setInterruptState } = useWorkflowMetadata();
  
  const [escPressCount, setEscPressCount] = useState(0);
  const [showInterruptModal, setShowInterruptModal] = useState(false);
  const lastEscPressRef = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle ESC key press
  const handleEscape = useCallback((event: KeyboardEvent) => {
    if (!enabled || event.key !== 'Escape') return;

    const now = Date.now();
    const timeSinceLastPress = now - lastEscPressRef.current;

    // Reset count if more than 2 seconds since last press
    if (timeSinceLastPress > 2000) {
      setEscPressCount(1);
      setShowInterruptModal(true);
      lastEscPressRef.current = now;

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set timeout to reset after 2 seconds
      timeoutRef.current = setTimeout(() => {
        setEscPressCount(0);
        setShowInterruptModal(false);
      }, 2000);
    } else {
      // Second ESC press within 2 seconds
      setEscPressCount(2);
      handleInterrupt();
    }
  }, [enabled]);

  // Confirm interrupt
  const handleInterrupt = useCallback((reason?: string) => {
    setInterruptState({
      isInterrupting: true,
      interruptedAt: new Date().toISOString(),
      reason
    });
    
    setShowInterruptModal(false);
    setEscPressCount(0);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    onInterrupt?.(reason);
  }, [setInterruptState, onInterrupt]);

  // Cancel interrupt
  const cancelInterrupt = useCallback(() => {
    setShowInterruptModal(false);
    setEscPressCount(0);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  // Resume workflow
  const resumeWorkflow = useCallback(() => {
    setInterruptState({
      isInterrupting: false,
      canResume: false,
      savedContext: undefined,
      interruptedAt: undefined,
      reason: undefined
    });

    onResume?.();
  }, [setInterruptState, onResume]);

  // Set up ESC listener
  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, handleEscape]);

  return {
    isInterrupting: interrupt.isInterrupting,
    canResume: interrupt.canResume,
    showInterruptModal,
    escPressCount,
    handleInterrupt,
    cancelInterrupt,
    resumeWorkflow
  };
};